import { c as _c } from "react/compiler-runtime";
import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IcoDragAndDrop16, IcoPlusSmall16 } from '@onefootprint/icons';
import { Dialog, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type ComponentProps, useId, useRef, useState } from 'react';
import type { AgentProcedureBlockConfig } from '@/components/block-primitives/contracts/agent-blocks';
import BlockShell from '../block-shell';
import ListRow from '../list-row';
import TitleField from '../title-field';
type AgentProcedureBlockProps = {
  config: AgentProcedureBlockConfig;
  editable?: boolean;
  justInserted?: boolean;
  onChange?: (next: AgentProcedureBlockConfig) => void;
  onDelete?: () => void;
  onExit?: () => void;
  requireSteps?: boolean;
};
const AgentProcedureBlock = ({
  config,
  editable = false,
  justInserted = false,
  onChange,
  onDelete,
  onExit,
  requireSteps = false
}: AgentProcedureBlockProps) => {
  const [pending, setPending] = useState(false);
  const [confirming, setConfirming] = useState<number | null>(null);
  const dndId = useId();
  const everFilled = useRef(false);
  const firstStep = useRef<{
    focus: () => void;
  }>(null);
  everFilled.current ||= config.steps.length > 0;
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const rows = rowsFor(config.steps, editable);
  const removable = rows.length > 1;
  const sortable = editable && config.steps.length > 1;
  const patch = (next: Partial<AgentProcedureBlockConfig>) => {
    onChange?.({
      ...config,
      ...next
    });
  };
  const removeStep = (index: number) => {
    if (index >= config.steps.length) return;
    patch({
      steps: config.steps.filter((_, i) => i !== index)
    });
  };
  const abandonRow = (index_0: number) => {
    if (!requireSteps && justInserted && !everFilled.current) {
      onDelete?.();
      return;
    }
    removeStep(index_0);
  };
  const submitStep = (index_1: number, next_0: string) => {
    if (!next_0) return;
    patch({
      steps: rows.map((step, i_0) => i_0 === index_1 ? next_0 : step)
    });
  };
  const submitStepLines = (index_2: number, lines: string[]) => {
    patch({
      steps: [...rows.slice(0, index_2), ...lines, ...rows.slice(index_2 + 1)]
    });
  };
  const submitPending = (next_1: string) => {
    if (!next_1) {
      setPending(false);
      return;
    }
    patch({
      steps: [...config.steps, next_1]
    });
  };
  const handleDragEnd = ({
    active,
    over
  }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    patch({
      steps: reorderedSteps(config.steps, String(active.id), String(over.id))
    });
  };
  const blockDeletionCopy = () => {
    const count = config.steps.length;
    if (!count) {
      return 'It holds no steps yet.';
    }
    if (count === 1) {
      return 'Its only step goes with it, and the agent will no longer be asked to follow it.';
    }
    return `Its ${count} steps go with it, and the agent will no longer be asked to follow them.`;
  };
  const stepRowProps = (index_3: number, step_0: string): ComponentProps<typeof ListRow> => ({
    autoFocus: !requireSteps && justInserted && !config.steps.length,
    className: 'relative',
    editable,
    editorRef: index_3 === 0 ? firstStep : undefined,
    label: `Step ${index_3 + 1}`,
    marker: <StepNumber count={rows.length} index={index_3} value={step_0} />,
    onAdvance: !pending && index_3 === rows.length - 1 ? () => setPending(true) : undefined,
    onSubmit: (next_2: string) => submitStep(index_3, next_2),
    onSubmitLines: (lines_0: string[]) => submitStepLines(index_3, lines_0),
    onEmptyBlur: () => abandonRow(index_3),
    onRemove: editable && removable ? () => setConfirming(index_3) : undefined,
    placeholder: NEW_STEP,
    removeLabel: `Remove step ${index_3 + 1}`,
    value: step_0
  });
  const list = <ol className="flex flex-col gap-0.5">
      {rows.map((step_1, index_4) => sortable ?
    // biome-ignore lint/suspicious/noArrayIndexKey: steps are user-editable strings, so position is the stable identity
    <SortableStepRow index={index_4} key={index_4} rowProps={stepRowProps(index_4, step_1)} /> :
    // biome-ignore lint/suspicious/noArrayIndexKey: steps are user-editable strings, so position is the stable identity
    <ListRow key={index_4} {...stepRowProps(index_4, step_1)} />)}
      {pending && <ListRow autoFocus className="mt-2 min-h-7" editable
    // Keyed by what has been committed, so each Enter mounts a fresh row that takes the caret
    // from the one it replaces — `autoFocus` only seeds the field's initial state.
    key={`pending-${config.steps.length}`} label="New step" marker={<StepNumber count={config.steps.length + 1} index={config.steps.length} value="" />} onAdvance={() => setPending(true)} onSubmit={submitPending} onSubmitLines={lines_1 => patch({
      steps: [...config.steps, ...lines_1]
    })} onEmptyBlur={() => setPending(false)} onRemove={() => setPending(false)} placeholder={NEW_STEP} removeLabel="Remove new step" value="" />}
      {editable && !pending && config.steps.length > 0 && <li className="mt-2 flex min-h-7 items-center">
          <LinkButton iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => setPending(true)} variant="label-2">
            Add a step
          </LinkButton>
        </li>}
    </ol>;
  return <BlockShell onExit={onExit} editable={editable} label="Procedure" onDelete={onDelete && {
    confirmation: {
      title: 'Delete procedure?',
      description: blockDeletionCopy()
    },
    onConfirm: onDelete
  }} title={config.title ?? undefined}>
      <TitleField autoFocus={requireSteps && justInserted} editable={editable} label="Procedure title" onEnter={requireSteps ? () => firstStep.current?.focus() : undefined} onSubmit={title => patch({
      title: title || undefined
    })} value={config.title ?? undefined} />

      {(rows.length > 0 || pending) && (sortable ? <DndContext collisionDetection={closestCenter} id={dndId} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
            <SortableContext items={rows.map((__0, index_5) => stepId(index_5))} strategy={verticalListSortingStrategy}>
              {list}
            </SortableContext>
          </DndContext> : list)}

      <Dialog isConfirmation onClose={() => setConfirming(null)} open={confirming !== null} primaryButton={{
      label: 'Yes, delete',
      onClick: () => {
        if (confirming !== null) removeStep(confirming);
        setConfirming(null);
      },
      variant: 'destructive'
    }} secondaryButton={{
      label: 'No',
      onClick: () => setConfirming(null)
    }} size="compact" title="Delete step?">
        <p className="text-center text-body-2 text-secondary">The agent will no longer be asked to follow it.</p>
      </Dialog>
    </BlockShell>;
};
const reorderedSteps = (steps: string[], activeId: string, overId: string) => {
  return arrayMove(steps, stepIndex(activeId), stepIndex(overId));
};
const NEW_STEP = 'Describe this step';
const stepId = (index: number) => `step-${index}`;
const stepIndex = (id: string) => Number(id.replace('step-', ''));
const rowsFor = (steps: string[], editable: boolean) => {
  if (steps.length) {
    return steps;
  }
  if (editable) {
    return [''];
  }
  return [];
};
type StepNumberProps = {
  count: number;
  index: number;
  value: string;
};

// Past nine the number outgrows the 16px marker column; every row in the list takes the wider one so
// their labels stay flush.
const StepNumber = t0 => {
  const $ = _c(6);
  const {
    count,
    index,
    value
  } = t0;
  const t1 = count > 9 && "min-w-6";
  const t2 = value ? "text-primary" : "text-quaternary";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("text-body-2 text-right tabular-nums", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = index + 1;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <span className={t3}>{t4}.</span>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
type SortableStepRowProps = {
  index: number;
  rowProps: ComponentProps<typeof ListRow>;
};
const SortableStepRow = t0 => {
  const $ = _c(20);
  const {
    index,
    rowProps
  } = t0;
  let t1;
  if ($[0] !== index) {
    t1 = stepId(index);
    $[0] = index;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      id: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t2);
  const t3 = `Reorder step ${index + 1}`;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IcoDragAndDrop16 color="tertiary" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== attributes || $[6] !== listeners || $[7] !== t3) {
    t5 = <button {...attributes} {...listeners} aria-label={t3} className="absolute -left-5 top-1 flex h-5 w-4 shrink-0 cursor-grab touch-none items-center justify-center opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-70 hover:opacity-100 in-data-force-affordance:opacity-70 max-md:opacity-70 max-md:after:absolute max-md:after:-inset-1.5" type="button">{t4}</button>;
    $[5] = attributes;
    $[6] = listeners;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== transform) {
    t6 = CSS.Transform.toString(transform);
    $[9] = transform;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isDragging ? 0.5 : 1;
  let t8;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== transition) {
    t8 = {
      transform: t6,
      transition,
      opacity: t7
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = transition;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== rowProps || $[16] !== setNodeRef || $[17] !== t5 || $[18] !== t8) {
    t9 = <ListRow {...rowProps} leading={t5} ref={setNodeRef} style={t8} />;
    $[15] = rowProps;
    $[16] = setNodeRef;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};
export default AgentProcedureBlock;
export { reorderedSteps };